import {
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    Stack,
} from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
    EXPERIMENT_MISSION_PROCESSING_STATUS,
    HEIGHT_AND_BIOVOLUME_RGB_TGDT_UUID,
    ORTHOIMAGE_RGB_TGDT_UUID,
    PAGINATION,
    PIPELINE_INSTANCE_STATUS,
    POINT_CLOUD_EXTRACTION_RGB_TGDT_UUID,
    SENESCENCE_RGB_TGDT_UUID,
    TRAIT_GROUP_ASSOCIABLE_TYPES,
} from "../../../constants";
import { useCallback, useEffect, useMemo, useState } from "react";

import { BACKEND_ROUTES } from "../../../backendRoutes";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ChevronRight } from "@mui/icons-material";
import ErrorIcon from "@mui/icons-material/Error";
import { ExperimentMissionBreadcrumbs } from "../../../components";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import { LoadingButton } from "@mui/lab";
import { PipelineInstanceItem } from "./PipelineInstanceItem";
import PropTypes from "prop-types";
import { TraitGroupList } from "./TraitGroupList";
import { TraitGroupListSkeleton } from "./TraitGroupListSkeleton";
import { hasPointCloud } from "./utils";
import useSWR from "swr";

export const TraitGroupModal = ({ closeModal, experimentMissionUuid }) => {
    const {
        data: experimentMission,
        error: experimentMissionFetchError,
        isLoading: experimentMissionIsLoading,
    } = useSWR(
        experimentMissionUuid
            ? `${BACKEND_ROUTES.EXPERIMENT_MISSION}/${experimentMissionUuid}?parentInfo=true&compatibleExperimentMissions=true`
            : null
    );

    const associatedTraitGroupMSearchParams = new URLSearchParams({
        associableType: TRAIT_GROUP_ASSOCIABLE_TYPES.MISSION,
        associableUuid: experimentMission?.missionUuid,
        // There shouldn't be more than 200 trait groups associated to an experiment mission
        limit: PAGINATION.GENERIC.LIMIT.MAX,
        pipelineInstance: true,
        validatedPipelineInstance: true,
        traitGroup: true,
    });

    const associatedTraitGroupEMSearchParams = new URLSearchParams({
        associableType: TRAIT_GROUP_ASSOCIABLE_TYPES.EXPERIMENT_MISSION,
        associableUuid: experimentMission?.uuid,
        // There shouldn't be more than 200 trait groups associated to an experiment mission
        limit: PAGINATION.GENERIC.LIMIT.MAX,
        pipelineInstance: true,
        validatedPipelineInstance: true,
        traitGroup: true,
    });

    const {
        data: missionTraitGroups,
        error: missionTraitGroupsFetchError,
        isLoading: missionTraitGroupsIsLoading,
        mutate: mutateMissionTraitGroups,
    } = useSWR(
        experimentMission
            ? `${BACKEND_ROUTES.ASSOCIATED_TRAIT_GROUP}?${associatedTraitGroupMSearchParams}`
            : null
    );

    const {
        data: experimentMissionTraitGroups,
        error: experimentMissionTraitGroupsFetchError,
        isLoading: experimentMissionTraitGroupsIsLoading,
        mutate: mutateExperimentMissionTraitGroups,
    } = useSWR(
        experimentMission
            ? `${BACKEND_ROUTES.ASSOCIATED_TRAIT_GROUP}?${associatedTraitGroupEMSearchParams}`
            : null
    );

    const {
        data: pipelines,
        error: pipelinesFetchError,
        isLoading: pipelinesIsLoading,
    } = useSWR(
        experimentMissionTraitGroups
            ? `${BACKEND_ROUTES.PIPELINE}?${createListPipelineSearchParams({
                  experimentMission,
                  experimentMissionTraitGroups:
                      experimentMissionTraitGroups.rows,
              })}`
            : null,
        { keepPreviousData: true }
    );

    const [hideNoPipelines, setHideNoPipelines] = useState(false);
    const [instantiateAll, setInstantiateAll] = useState(false);

    const compatibleExperimentMissions = useMemo(
        () =>
            (experimentMission?.compatibleExperimentMissions ?? []).filter(
                (cem) =>
                    ![
                        EXPERIMENT_MISSION_PROCESSING_STATUS.SUCCESS,
                        EXPERIMENT_MISSION_PROCESSING_STATUS.ABORTED,
                    ].includes(cem.processingStatus)
            ),
        [experimentMission?.compatibleExperimentMissions]
    );

    const formMethods = useForm({
        defaultValues: {
            parentPipelineInstance: "",
        },
    });

    const {
        formState: { isSubmitting },
        control,
        setValue,
        watch,
    } = formMethods;

    const parentPipelineInstance = watch("parentPipelineInstance");
    const traitGroupsWatch = watch("traitGroups");

    const successMutate = useCallback(() => {
        mutateMissionTraitGroups();
        mutateExperimentMissionTraitGroups();
    }, [mutateExperimentMissionTraitGroups, mutateMissionTraitGroups]);

    // If point cloud extraction (RGB), height & biovolume (RGB) or senescence
    // (RGB) is selected only pipeline instances of Orthoimage-RGB with point
    // cloud can be used
    const needsPointCloud = (experimentMissionTraitGroups?.rows ?? []).some(
        (emtg) => {
            // Only these TGDTs force the point-cloud constraint
            if (
                ![
                    POINT_CLOUD_EXTRACTION_RGB_TGDT_UUID,
                    HEIGHT_AND_BIOVOLUME_RGB_TGDT_UUID,
                    SENESCENCE_RGB_TGDT_UUID,
                ].includes(emtg.traitGroupDataTypeUuid)
            )
                return false;

            // Use the live form value once the checkbox has registered it;
            // before that, fall back to the TraitGroup default (checked when
            // the group has at least one pipeline).
            const checked = traitGroupsWatch?.[emtg.uuid]?.checked;
            if (checked !== undefined) return checked;

            return (pipelines?.rows ?? []).some(
                (p) => p.traitGroupDataTypeUuid === emtg.traitGroupDataTypeUuid
            );
        }
    );

    // Merge pipeline instances from experimentMissionTraitGroups
    const allPipelineInstances = useMemo(() => {
        if (!experimentMissionTraitGroups?.rows && !missionTraitGroups?.rows)
            return [];

        const instancesMap = new Map();

        const associatedTraitGroups = [
            ...(experimentMissionTraitGroups?.rows ?? []),
            ...(missionTraitGroups?.rows ?? []),
        ];

        associatedTraitGroups.forEach((atg) => {
            if (atg.PipelineInstances) {
                atg.PipelineInstances.forEach((instance) => {
                    // Only include finished, aborted, or error status instances
                    if (
                        [
                            PIPELINE_INSTANCE_STATUS.FINISHED,
                            PIPELINE_INSTANCE_STATUS.ERROR,
                            PIPELINE_INSTANCE_STATUS.ABORTED,
                        ].includes(instance.status)
                    ) {
                        if (instancesMap.has(instance.uuid)) {
                            instancesMap
                                .get(instance.uuid)
                                .traitGroupDataTypes.push(
                                    atg.TraitGroupDataType
                                );
                        } else {
                            instancesMap.set(instance.uuid, {
                                ...instance,
                                traitGroupDataTypes: [atg.TraitGroupDataType],
                            });
                        }
                    }
                });
            }
        });

        // Currently, only pipeline instances of Orthoimage-RGB should be used
        // for parent pipeline instance
        let orthoRgbPipelineInstances = Array.from(
            instancesMap.values()
        ).filter((instance) =>
            instance.traitGroupDataTypes.some(
                (tgdt) => tgdt.uuid === ORTHOIMAGE_RGB_TGDT_UUID
            )
        );

        if (needsPointCloud)
            orthoRgbPipelineInstances = orthoRgbPipelineInstances.filter(
                (instance) => hasPointCloud(instance)
            );

        // Sort by createdAt (most recent first)
        return orthoRgbPipelineInstances.sort((a, b) => {
            if (!a.createdAt || !b.createdAt) return 0;
            return new Date(b.createdAt) - new Date(a.createdAt);
        });
    }, [experimentMissionTraitGroups, missionTraitGroups, needsPointCloud]);

    // Set default selection, and keep it consistent with the available
    // instances (e.g. when point cloud filtering removes the current one)
    useEffect(() => {
        if (allPipelineInstances.length === 0) {
            // Clear any stale selection when nothing is selectable
            if (parentPipelineInstance) {
                setValue("parentPipelineInstance", "");
            }
            return;
        }

        // Keep the current selection only if it is still available;
        // otherwise default to the most recent instance
        const isSelectionValid = allPipelineInstances.some(
            (instance) => instance.uuid === parentPipelineInstance
        );
        if (!isSelectionValid) {
            setValue("parentPipelineInstance", allPipelineInstances[0].uuid);
        }
    }, [allPipelineInstances, parentPipelineInstance, setValue]);

    const mergedFetchError =
        experimentMissionFetchError ??
        missionTraitGroupsFetchError ??
        experimentMissionTraitGroupsFetchError ??
        pipelinesFetchError;
    const hasError = Boolean(!experimentMission || mergedFetchError);

    const isLoading =
        experimentMissionIsLoading ||
        missionTraitGroupsIsLoading ||
        experimentMissionTraitGroupsIsLoading ||
        pipelinesIsLoading;

    return (
        <FormProvider {...formMethods}>
            {experimentMission && (
                <DialogTitle>
                    <ExperimentMissionBreadcrumbs
                        experimentMission={experimentMission}
                    />
                </DialogTitle>
            )}
            <DialogContent>
                {hasError ? (
                    <FetchErrorAlert
                        error={mergedFetchError ?? { status: 404 }}
                    />
                ) : isLoading ? (
                    <TraitGroupListSkeleton />
                ) : (
                    <TraitGroupList
                        experimentMissionTraitGroups={
                            experimentMissionTraitGroups.rows
                        }
                        pipelines={pipelines.rows}
                        experimentMission={experimentMission}
                        successMutate={successMutate}
                        hideNoPipelines={hideNoPipelines}
                        parentPipelineInstance={parentPipelineInstance}
                        instantiateAll={instantiateAll}
                        compatibleExperimentMissions={
                            compatibleExperimentMissions
                        }
                    />
                )}
            </DialogContent>
            <DialogActions>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={hideNoPipelines}
                            onChange={(e) =>
                                setHideNoPipelines(e.target.checked)
                            }
                            disabled={hasError || isLoading}
                        />
                    }
                    label="Hide orphan trait groups"
                    sx={{ mr: "auto", ml: 1 }}
                />
                <Controller
                    name="parentPipelineInstance"
                    control={control}
                    rules={{
                        required: "Parent pipeline instance is required",
                    }}
                    render={({ field, fieldState: { error } }) => (
                        <FormControl
                            sx={{ minWidth: 400, mr: 1 }}
                            size="small"
                            error={Boolean(error)}
                        >
                            <InputLabel id="parent-pipeline-instance-label">
                                Parent Pipeline Instance
                            </InputLabel>
                            <Select
                                {...field}
                                labelId="parent-pipeline-instance-label"
                                id="parent-pipeline-instance"
                                label="Parent Pipeline Instance"
                                disabled={
                                    hasError ||
                                    isLoading ||
                                    allPipelineInstances.length === 0
                                }
                                renderValue={(selected) => {
                                    if (!selected) return null;

                                    const selectedInstance =
                                        allPipelineInstances.find(
                                            (inst) => inst.uuid === selected
                                        );
                                    if (!selectedInstance) return selected;

                                    return (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            width={1}
                                        >
                                            {selectedInstance.status ===
                                            PIPELINE_INSTANCE_STATUS.FINISHED ? (
                                                <CheckCircleIcon
                                                    color="success"
                                                    fontSize="small"
                                                />
                                            ) : (
                                                <ErrorIcon
                                                    color="error"
                                                    fontSize="small"
                                                />
                                            )}
                                            <Box flex={1}>
                                                <PipelineInstanceItem
                                                    pipelineInstance={
                                                        selectedInstance
                                                    }
                                                    asLink={false}
                                                />
                                            </Box>
                                        </Stack>
                                    );
                                }}
                            >
                                {allPipelineInstances.map((instance) => (
                                    <MenuItem
                                        key={instance.uuid}
                                        value={instance.uuid}
                                    >
                                        <ListItemIcon>
                                            {instance.status ===
                                            PIPELINE_INSTANCE_STATUS.FINISHED ? (
                                                <CheckCircleIcon
                                                    color="success"
                                                    fontSize="small"
                                                />
                                            ) : (
                                                <ErrorIcon
                                                    color="error"
                                                    fontSize="small"
                                                />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText>
                                            <PipelineInstanceItem
                                                isCtrlClickable
                                                pipelineInstance={instance}
                                                asLink={false}
                                            />
                                        </ListItemText>
                                    </MenuItem>
                                ))}
                            </Select>
                            {error && (
                                <FormHelperText>{error.message}</FormHelperText>
                            )}
                        </FormControl>
                    )}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={instantiateAll}
                            onChange={(e) =>
                                setInstantiateAll(e.target.checked)
                            }
                            disabled={
                                hasError ||
                                isLoading ||
                                compatibleExperimentMissions.length === 0
                            }
                        />
                    }
                    label={
                        compatibleExperimentMissions.length === 0
                            ? "No other compatible experiment"
                            : `Instantiate for the other ${compatibleExperimentMissions.length} compatible experiments`
                    }
                />
                <Button onClick={closeModal}>Close</Button>
                <LoadingButton
                    type="submit"
                    variant="contained"
                    form="traitGroups"
                    endIcon={<ChevronRight />}
                    disabled={
                        hasError ||
                        isLoading ||
                        // If not loading and no error `experimentMissionTraitGroups` is defined
                        !experimentMissionTraitGroups.rows.length
                    }
                    loading={isSubmitting}
                >
                    Instantiate
                </LoadingButton>
            </DialogActions>
        </FormProvider>
    );
};

const createListPipelineSearchParams = ({
    experimentMission,
    experimentMissionTraitGroups,
}) =>
    `system=${
        experimentMission.Mission.AcquisitionVector.SystemModel.system
    }&${experimentMissionTraitGroups
        .map(
            ({ TraitGroupDataType: { uuid } }) =>
                `traitGroupDataTypeUuids[]=${uuid}`
        )
        .join("&")}`;

TraitGroupModal.propTypes = {
    closeModal: PropTypes.func.isRequired,
    experimentMissionUuid: PropTypes.string,
};
